import {
  getCachedSelfAppDataOptions,
  isDashboardAdminGrant,
  MyVenue,
  Roles,
  useRefreshCurrentUser,
  useVenue,
} from '@idu-nightlife/ui-kit';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  configs,
  hasResolvableUrl,
  nightlifeConfigs,
  nightlifeDashboardConfig,
  type AppConfig,
} from './app-config';
import {
  useNightlifeVenues,
  useRefreshNightlifeVenues,
  type NightlifeVenueList,
} from './use-nightlife-venues';

export type AvailableAppVenue = {
  id: string;
  name: string;
  slug: string;
};

export type AvailableAppItem = {
  application: AppConfig['id'];
  name: string; // application name
  logo: AppConfig['icon'];
  color: string;
  url: string;
  /** Null on a Checkd grant, which carries `roleName` instead (spec 045 FR-038). */
  role: Roles | null;
  roleId?: string | null;
  /** Shown in place of the role: a Checkd grant's role name, or a nightlife venue's project. */
  roleName?: string | null;
  venue: AvailableAppVenue;
};

/** The user's grant list plus whether it is the authoritative one for this session (FR-013). */
export type AvailableApps = {
  items: AvailableAppItem[];
  isSettled: boolean;
  /** Apps whose own venue list has not answered yet; only a selection that depends on one waits for it. */
  unsettledAppIds: AppConfig['id'][];
  /** Apps whose venue list could not be fetched; an address in one goes to the app, which says why. */
  unavailableAppIds: AppConfig['id'][];
};

const grantItems = (venues: MyVenue[]): AvailableAppItem[] =>
  venues.flatMap((venue): AvailableAppItem[] => {
    const config = configs.find((conf) => conf.id === venue.application);
    if (!config || !hasResolvableUrl(config)) {
      return [];
    }

    return [
      {
        color: config.color,
        logo: config.icon,
        url: config.url,
        name: config.name,

        application: venue.application,
        role: venue.role,
        roleId: venue.roleId,
        roleName: venue.roleName,
        venue,
      },
    ];
  });

// A nightlife manager holds every venue alike; the portal enforces each venue's own permissions.
const nightlifeVenueItems = (lists: NightlifeVenueList[]): AvailableAppItem[] =>
  lists.flatMap(({ config, venues }) =>
    venues.map(({ venueId, name }): AvailableAppItem => ({
      color: config.color,
      logo: config.icon,
      url: config.url,
      name: config.name,

      application: config.id,
      role: 'manager',
      // Every nightlife venue is managed, so the label names its project instead of the role.
      roleName: config.key.toUpperCase(),
      venue: { id: venueId, name, slug: encodeURIComponent(venueId) },
    })),
  );

/** One choice per nightlife project for an iDU Dashboard admin: the grant the dashboard checks before it signs anyone in. */
const nightlifeDashboardItems = (venues: MyVenue[]): AvailableAppItem[] => {
  const config = nightlifeDashboardConfig;
  if (!venues.some(isDashboardAdminGrant) || !hasResolvableUrl(config)) return [];

  return nightlifeConfigs.map(({ key }) => ({
    color: config.color,
    logo: config.icon,
    url: config.url,
    name: config.name,

    application: config.id,
    role: 'admin',
    venue: { id: key, name: key.toUpperCase(), slug: key },
  }));
};

// iDU staff, CS above all, mostly work in the dashboards, so those lead the list (and become the default) for whoever holds them.
const DASHBOARD_APP_IDS: AppConfig['id'][] = ['idu_dashboard', nightlifeDashboardConfig.id];
const dashboardFirst = (item: AvailableAppItem) =>
  DASHBOARD_APP_IDS.includes(item.application) ? 0 : 1;

/**
 * Deterministic selector order: the dashboard products first, then ascending by application id, then venue name
 * (the manager URL scheme and default-app resolution rely on this order —
 * specs/016-global-nav-venue-sync/contracts/manager-url-scheme.md). Venues of an app this
 * deployment cannot address are dropped, so it is never offered, defaulted to or rendered.
 */
export function transformVenueDataToAppItems(
  venues: MyVenue[],
  nightlifeLists: NightlifeVenueList[] = [],
): AvailableAppItem[] {
  return [
    ...grantItems(venues),
    ...nightlifeVenueItems(nightlifeLists),
    ...nightlifeDashboardItems(venues),
  ].sort(
    (a, b) =>
      dashboardFirst(a) - dashboardFirst(b) ||
      a.application.localeCompare(b.application) ||
      (a.venue?.name ?? '').localeCompare(b.venue?.name ?? ''),
  );
}

/**
 * The list is settled only once a `/self` fetch has succeeded since this hook subscribed — the
 * cache can otherwise serve a list from a previous session (persisted for 24h, or an offline
 * snapshot on native), and a venue granted since then would look revoked (FR-013).
 */
export function useAvailableApps(): AvailableApps {
  const { allVenues } = useVenue();
  // The shared options disable refetch-on-mount, so a restored cache would never settle.
  const { isSuccess, isFetchedAfterMount } = useQuery({
    ...getCachedSelfAppDataOptions(),
    refetchOnMount: 'always',
  });

  const [isSettled, setIsSettled] = useState(false);
  const hasAuthoritativeList = isSuccess && isFetchedAfterMount;

  // Latched: an arrival cannot un-happen, and a later refetch failure must not drop the selection.
  useEffect(() => {
    if (hasAuthoritativeList) setIsSettled(true);
  }, [hasAuthoritativeList]);

  const nightlife = useNightlifeVenues();

  const items = useMemo(
    () => transformVenueDataToAppItems(allVenues || [], nightlife.lists),
    [allVenues, nightlife.lists],
  );

  return useMemo(
    () => ({
      items,
      isSettled,
      unsettledAppIds: nightlife.unsettledAppIds,
      unavailableAppIds: nightlife.unavailableAppIds,
    }),
    [items, isSettled, nightlife.unsettledAppIds, nightlife.unavailableAppIds],
  );
}

/** Fetches every venue source again, so the selector offers grants made since it last opened. */
export function useRefreshAvailableApps() {
  const refreshSelf = useRefreshCurrentUser();
  const refreshNightlife = useRefreshNightlifeVenues();

  return () => {
    void refreshSelf();
    void refreshNightlife();
  };
}
